import { ApiOkResponse } from 'apisauce';
import { extractOrderFromValues } from '../../../../../components/Crud/useFilter.utils';
import { UseInfinityRawCountDTO } from '../../../../../components/Hooks/infinity-list/useInfinityCount';
import { fetcher } from '../../../../../components/Hooks/useListActions';
import { UseInfiniteListWithCheckboxParams } from '../../../../../hooks/useInfiniteListWithCheckbox/useInfiniteListWithCheckbox.types';
import { restApi } from '../../../../../services/api';
import getRoutePrefix from '../../../../../services/utils/getRoutePrefix.util';
import { cancelTokenFromSignal } from '../../../../../utils/axios.utils';
import { existsIndexDbToUser } from '../../../../offline/database/offlineDatabase/offlineDatabase';
import { orderRepositoryOfflineAdapters } from '../../../../offline/offlineRequestAdapters/order.repository.offlineAdapters';
import { formatOrdersWithChildren } from '../../../formatOrders';
import { OrderListTabName } from '../../../list/orderListTabs';
import { OrderListType, OrderListWithBaseItem } from '../../../order.types';

export const KEY_TO_DETECT_TAB_TO_OFFLINE = 'stn';

const formatOrderWithChildrenResponse = (
  response: ApiOkResponse<OrderListType[]>,
): ApiOkResponse<OrderListWithBaseItem[]> => ({
  ...response,
  data: formatOrdersWithChildren(response?.data || []),
});

const getOfflineOrdersDataHandler = async () => {
  try {
    const { data: offlineOrders, count } = await orderRepositoryOfflineAdapters.ordersList({
      limit: 999,
      skip: 0,
    });

    return {
      count,
      data: formatOrdersWithChildren(offlineOrders),
    };
  } catch (_) {
    return {
      count: 0,
      data: [],
    };
  }
};

export const ACCEPTED_TABS_TO_FETCH_OFFLINE = [OrderListTabName.ALL, OrderListTabName.OPEN];

const verifyIfShouldFetchOfflineOrders = async (params?: Record<string, any>) => {
  const shouldFetchOfflineOrders =
    ACCEPTED_TABS_TO_FETCH_OFFLINE.includes(params?.[KEY_TO_DETECT_TAB_TO_OFFLINE]) &&
    (await existsIndexDbToUser());

  return shouldFetchOfflineOrders;
};

export const orderListFnToInfiniteListWithCheckbox: UseInfiniteListWithCheckboxParams<OrderListWithBaseItem>['queryFn'] =
  async ({ limit, order, skip, params, signal }) => {
    const cancelToken = cancelTokenFromSignal(signal)?.token;

    const shouldFetchOfflineOrders = (await verifyIfShouldFetchOfflineOrders(params)) && skip === 0;
    const fetcherFn = fetcher<OrderListType[]>(
      `${getRoutePrefix()}/orders-list/page`,
      formatOrderWithChildrenResponse,
    );

    const [offlineOrdersData, countResponse, orderListResponse] = await Promise.all([
      shouldFetchOfflineOrders ? getOfflineOrdersDataHandler() : { count: 0, data: [] },
      skip === 0
        ? restApi.get<UseInfinityRawCountDTO>(`${getRoutePrefix()}/orders-list/count`, params, {
            cancelToken,
          })
        : Promise.resolve({ ok: true, data: undefined } as ApiOkResponse<UseInfinityRawCountDTO>),
      fetcherFn({
        count: limit,
        page: Math.floor(skip / limit),
        config: { cancelToken },
        params: {
          ...params,
          order: extractOrderFromValues(params) || order,
        },
      }),
    ]);

    if (!countResponse.ok) {
      throw countResponse.originalError;
    }
    if (orderListResponse.tag === 'error') {
      throw new Error(orderListResponse.error);
    }

    const countData = countResponse.data;
    const orderListData = orderListResponse.data;

    const dataWithOfflineOrders = [...offlineOrdersData?.data, ...orderListData];

    return {
      data: dataWithOfflineOrders,
      countData: {
        total: (countData?.count || 0) + offlineOrdersData.count,
        virtualTotal: (countData?.rawCount || 0) + offlineOrdersData.count,
      },
    };
  };

export const offlineOrderListFnToInfiniteListWithCheckbox: UseInfiniteListWithCheckboxParams<OrderListWithBaseItem>['queryFn'] =
  async ({ limit, skip }) => {
    const { data: offlineOrders, count } = await orderRepositoryOfflineAdapters.ordersList({
      limit,
      skip,
    });

    return {
      data: formatOrdersWithChildren(offlineOrders),
      countData: { total: count, virtualTotal: count },
    };
  };
